import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Loader2, CalendarDays, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ActivitySummary = ({ isGuestMode, getAuthHeaders, onGuestAction }) => {
  const navigate = useNavigate();
  const [todaySummary, setTodaySummary] = useState(null);
  const [weekSummary, setWeekSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isGuestMode) {
      setTodaySummary({
        privateEvents: [{ id: '1', title: 'Demo Event' }],
        publicEvents: [],
        tasksDueToday: [{ id: '1', text: 'Sample Task' }],
        overdueTasks: [],
      });
      setWeekSummary({
        days: Array.from({ length: 7 }, (_, i) => {
          const d = new Date();
          d.setDate(d.getDate() + i);
          return {
            date: d.toISOString().split('T')[0],
            dayName: d.toLocaleDateString('en-US', { weekday: 'short' }),
            events: i === 0 ? [{ id: '1' }] : [],
            tasks: i < 3 ? [{ id: '1' }] : []
          };
        })
      });
      setLoading(false);
      return;
    }
    const fetchSummaries = async () => {
      try {
        const [todayRes, weekRes] = await Promise.all([
          axios.get(`${API}/personal/today-summary`, { headers: getAuthHeaders() }),
          axios.get(`${API}/personal/week-summary`, { headers: getAuthHeaders() })
        ]);
        setTodaySummary(todayRes.data);
        setWeekSummary(weekRes.data);
      } catch (error) {
        console.error('Failed to load summaries:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSummaries();
  }, [getAuthHeaders, isGuestMode]);

  if (loading) {
    return (
      <Card><CardContent className="p-4 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></CardContent></Card>
    );
  }

  const todayEventCount = (todaySummary?.privateEvents?.length || 0) + (todaySummary?.publicEvents?.length || 0);
  const todayTaskCount = todaySummary?.tasksDueToday?.length || 0;
  const overdueCount = todaySummary?.overdueTasks?.length || 0;

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const busyDays = weekSummary?.days?.filter(d => d.events.length > 0 || d.tasks.length > 0).length || 0;
  const weekEventTotal = weekSummary?.days?.reduce((sum, d) => sum + d.events.length, 0) || 0;
  const weekTaskTotal = weekSummary?.days?.reduce((sum, d) => sum + d.tasks.length, 0) || 0;

  return (
    <div className="grid grid-cols-2 gap-3">
      {/* Today Card */}
      <Card
        className="cursor-pointer hover:shadow-md transition-all border hover:border-primary/30"
        onClick={() => isGuestMode ? onGuestAction() : navigate('/calendar')}
        data-testid="today-summary-card"
      >
        <CardContent className="p-4 space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Today</p>
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">{todayEventCount} event{todayEventCount !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            <span className="text-sm font-medium">{todayTaskCount} task{todayTaskCount !== 1 ? 's' : ''}</span>
          </div>
          {overdueCount > 0 && (
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <span className="text-sm font-medium text-red-500">{overdueCount} overdue</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* This Week Card */}
      <Card
        className="cursor-pointer hover:shadow-md transition-all border hover:border-primary/30"
        onClick={() => isGuestMode ? onGuestAction() : navigate('/calendar')}
        data-testid="week-summary-card"
      >
        <CardContent className="p-4 space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">This Week</p>
          <div className="flex items-center gap-1 h-8">
            {/* Mini week bar visualization */}
            {weekSummary?.days?.map((day, idx) => {
              const hasActivity = day.events.length > 0 || day.tasks.length > 0;
              return (
                <div key={idx} className="flex flex-col items-center flex-1">
                  <div
                    className={`w-full h-5 rounded-sm ${hasActivity ? 'bg-primary/60' : 'bg-muted'}`}
                    title={`${day.dayName}: ${day.events.length} events, ${day.tasks.length} tasks`}
                  />
                  <span className="text-[8px] text-muted-foreground mt-0.5">
                    {dayNames[new Date(day.date + 'T12:00:00').getDay()]?.[0]}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">
            {busyDays} busy day{busyDays !== 1 ? 's' : ''} · {weekEventTotal} event{weekEventTotal !== 1 ? 's' : ''} · {weekTaskTotal} task{weekTaskTotal !== 1 ? 's' : ''}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivitySummary;
